import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Target, Plus, ArrowRight, ArrowLeft, Loader2, AlertTriangle, ChevronRight, Building2, Trash2, Eye, Brain, Sparkles, Lightbulb, Check } from "lucide-react";
import { CompanyProfileBuilderDialog } from "@/components/ai-knowledge/CompanyProfileBuilderDialog";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { CampaignProfileBuilderDialog } from "@/components/ai-knowledge/CampaignProfileBuilderDialog";
import { getProfileCached, warmProfileBuilder } from "@/lib/profileBuilderApi";
import { CampaignKnowledgeDetailsDialog } from "@/components/ai-knowledge/CampaignKnowledgeDetailsDialog";
import type { CampaignKnowledge, CampaignGoals, CompanyKnowledgeProfile, BusinessProfile } from "@/components/ai-knowledge/types";
import type { Json } from "@/integrations/supabase/types";

// Extended type to include the guest token field
interface ExtendedCampaignKnowledge extends CampaignKnowledge {
  created_by_guest_token?: string | null;
  campaigns: { id: string; name: string; status: string };
}

interface AiSelectCampaignContextStepProps {
  workspaceId: string;
  companyProfileId: string;
  selectedCampaignKnowledgeId: string | null;
  onSelect: (id: string) => void;
  onNext: () => void;
  onBack: () => void;
  guestToken?: string;
}

const purposeTone = (p?: string | null) => {
  const k = (p || "").toLowerCase();
  if (k.includes("fund")) return "bg-warning/15 text-warning";
  if (k.includes("hire") || k.includes("job")) return "bg-info/15 text-info";
  if (k.includes("partner") || k.includes("distribut")) return "bg-accent text-accent-foreground";
  if (k.includes("event") || k.includes("network")) return "bg-secondary text-secondary-foreground";
  return "bg-success/15 text-success";
};

export function AiSelectCampaignContextStep({
  workspaceId,
  companyProfileId,
  selectedCampaignKnowledgeId,
  onSelect,
  onNext,
  onBack,
  guestToken,
}: AiSelectCampaignContextStepProps) {
  const [viewDetailsKnowledgeId, setViewDetailsKnowledgeId] = useState<string | null>(null);
  const [deleteKnowledgeId, setDeleteKnowledgeId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderEditId, setBuilderEditId] = useState<string | null>(null);
  // New builder is used only when the company profile was built and confirmed with it.
  const { data: factsStatus } = useQuery({
    queryKey: ["company-profile-facts-status", companyProfileId, guestToken ?? null],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("company-profile-builder", {
        body: { action: "get", profileId: companyProfileId, workspaceId: guestToken ? null : workspaceId, guestToken: guestToken ?? null },
      });
      if (error || data?.error) return "none";
      return (data?.facts_status as string) ?? "none";
    },
    enabled: !!companyProfileId,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const useBuilder = factsStatus === "confirmed";
  const needsSave = factsStatus !== undefined && factsStatus !== "confirmed";
  const [companyEditOpen, setCompanyEditOpen] = useState(false);

  // Fetch company profile for context display
  const { data: companyProfile } = useQuery({
    queryKey: ["company-knowledge-profile", companyProfileId, guestToken],
    queryFn: async () => {
      if (guestToken) {
        // Use RPC for guest access - get all profiles and find the one we need
        const { data, error } = await supabase.rpc('get_guest_company_profiles', {
          token_uuid: guestToken
        });
        if (error) throw error;
        return (data as CompanyKnowledgeProfile[])?.find(p => p.id === companyProfileId) || null;
      } else {
        const { data, error } = await supabase
          .from("company_knowledge_profiles")
          .select("*")
          .eq("id", companyProfileId)
          .single();

        if (error) throw error;
        return data as CompanyKnowledgeProfile;
      }
    },
    enabled: !!companyProfileId,
  });

  // Fetch campaign knowledge records linked to this company profile
  const { data: campaignKnowledge, isLoading } = useQuery({
    queryKey: ["campaign-knowledge-by-company", companyProfileId, guestToken],
    queryFn: async () => {
      if (guestToken) {
        // Use RPC for guest access - this returns campaign knowledge with created_by_guest_token
        const { data, error } = await supabase.rpc('get_guest_campaign_knowledge', {
          token_uuid: guestToken,
          company_profile_uuid: companyProfileId
        });
        if (error) throw error;
        // Transform to match expected format - use title as campaign name since guests don't have campaigns
        // Extract campaign purpose from campaign_goals for display fallback
        return (data || []).map((ck: any) => {
          const goals = ck.campaign_goals as Record<string, any> | null;
          const purposeLabel = goals?.campaign_purpose ? {
            sales: "Sales Campaign",
            hiring: "Hiring Campaign", 
            partner: "Partnership Campaign",
            branding: "Brand Campaign",
            job_hunting: "Job Search Campaign"
          }[goals.campaign_purpose] || "Campaign Strategy" : "Campaign Strategy";
          
          return {
            ...ck,
            campaigns: { 
              id: ck.campaign_id, 
              name: ck.title || purposeLabel, 
              status: 'draft' 
            }
          };
        }) as ExtendedCampaignKnowledge[];
      } else {
        const { data, error } = await supabase
          .from("campaign_knowledge")
          .select(`
            *,
            campaigns (
              id,
              name,
              status
            )
          `)
          .eq("company_profile_id", companyProfileId)
          .order("updated_at", { ascending: false });

        if (error) throw error;
        return data as ExtendedCampaignKnowledge[];
      }
    },
    enabled: !!companyProfileId,
  });

  // Delete mutation for campaign knowledge
  const deleteMutation = useMutation({
    mutationFn: async (knowledgeId: string) => {
      if (guestToken) {
        const { data, error } = await supabase.rpc('guest_delete_campaign_knowledge', {
          token_uuid: guestToken,
          knowledge_uuid: knowledgeId
        });
        if (error) throw error;
        return data;
      } else {
        // For authenticated users, delete via direct query
        const { error } = await supabase
          .from("campaign_knowledge")
          .delete()
          .eq("id", knowledgeId);
        if (error) throw error;
        return true;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-knowledge-by-company", companyProfileId, guestToken] });
      // Clear selection if deleted knowledge was selected
      if (deleteKnowledgeId === selectedCampaignKnowledgeId) {
        onSelect("");
      }
      setDeleteKnowledgeId(null);
      toast.success("Campaign strategy deleted successfully");
    },
    onError: (error: Error) => {
      toast.error("Failed to delete campaign strategy: " + error.message);
    },
  });

  const selectedKnowledge = campaignKnowledge?.find((k) => k.id === selectedCampaignKnowledgeId);
  // Drop a stale selection (deleted, or from another business).
  useEffect(() => {
    if (!companyProfileId) return;
    warmProfileBuilder();
    getProfileCached({ workspaceId: guestToken ? null : workspaceId ?? null, guestToken: guestToken ?? null, profileId: companyProfileId }).catch(() => undefined);
  }, [companyProfileId, workspaceId, guestToken]);

  useEffect(() => {
    if (campaignKnowledge && selectedCampaignKnowledgeId && !campaignKnowledge.some((k) => k.id === selectedCampaignKnowledgeId)) onSelect("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignKnowledge, selectedCampaignKnowledgeId]);
  const campaignGoals = selectedKnowledge?.campaign_goals as CampaignGoals | null;
  const businessProfile = companyProfile?.business_profile as BusinessProfile | null;

  const formatCampaignPurpose = (purpose?: string) => {
    const purposeMap: Record<string, string> = {
      sales: "Win new clients", sell: "Win new clients", win_clients: "Win new clients", agency: "Win new clients",
      hiring: "Hire people", hire: "Hire people",
      partner: "Find partners", partners: "Find partners",
      branding: "Brand building", networking: "Networking",
      event: "Promote an event", job_hunting: "Job search", job_search: "Job search",
      fundraising: "Raise funding", raise_funding: "Raise funding", other: "Other goal",
    };
    if (!purpose) return undefined;
    return purposeMap[purpose] || purpose.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="space-y-1">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-5 w-96" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-4">
              <Skeleton className="h-12 w-12 rounded-lg mb-3" />
              <Skeleton className="h-5 w-32 mb-2" />
              <Skeleton className="h-4 w-24" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Wait until we know which strategy screen to show, so the old one never flashes.
  if (!!companyProfileId && factsStatus === undefined) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-7 w-56 rounded bg-muted" />
        <div className="h-4 w-80 rounded bg-muted" />
        <div className="grid gap-4 sm:grid-cols-2"><div className="h-40 rounded-xl bg-muted" /><div className="h-40 rounded-xl bg-muted" /></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold tracking-tight">Campaign Strategy</h2>
          <p className="text-muted-foreground">
            Pick a saved campaign or create a new one with AI.
          </p>
        </div>
        {useBuilder && (
          <Button onClick={() => setBuilderOpen(true)} className="gap-2 shrink-0">
            <Plus className="h-4 w-4" /> New campaign
          </Button>
        )}
      </div>

      {needsSave && (
        <div className="flex flex-col gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4 sm:flex-row sm:items-center">
          <AlertTriangle className="h-5 w-5 shrink-0 text-warning" />
          <div className="flex-1 text-sm">
            <div className="font-medium">Finish your business profile to create campaigns</div>
            <div className="text-muted-foreground">Add your website, LinkedIn page, files or a short description, then save.</div>
          </div>
          <Button size="sm" onClick={() => setCompanyEditOpen(true)}>Finish & save</Button>
        </div>
      )}

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {campaignKnowledge?.map((knowledge) => {
            const isSelected = knowledge.id === selectedCampaignKnowledgeId;
            const hasLowScore = knowledge.knowledge_score < 100;
            const isComplete = knowledge.knowledge_score === 100;
            const goals = knowledge.campaign_goals as CampaignGoals | null;
            const isGuestOwned = guestToken && knowledge.created_by_guest_token === guestToken;
            const canEdit = !guestToken || isGuestOwned;

            return (
              <Card
                key={knowledge.id}
                className={cn(
                  "relative transition-all group border",
                  isComplete ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md hover:border-primary/50" : "cursor-not-allowed opacity-60",
                  isSelected && isComplete && "border-primary/60 bg-primary/[0.03]"
                )}
                onClick={() => isComplete && onSelect(knowledge.id)}
              >
                {isSelected && isComplete && (
                  <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Check className="h-3 w-3" />
                  </span>
                )}
                <CardContent className="flex h-full flex-col p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${purposeTone(goals?.campaign_purpose)}`}>
                      <Target className="h-3 w-3" />
                      {formatCampaignPurpose(goals?.campaign_purpose) || "Campaign"}
                    </span>
                    {hasLowScore && (
                      <span className="inline-flex items-center gap-1 text-xs text-warning"><AlertTriangle className="h-3 w-3" /> Incomplete</span>
                    )}
                  </div>
                  <h3 className="pr-6 text-base font-semibold leading-snug break-words">{knowledge.campaigns?.name || knowledge.title || 'Untitled Strategy'}</h3>
                  <div className="mt-auto flex items-center justify-between pt-4">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs text-muted-foreground">
                        {knowledge.updated_at && !isNaN(new Date(knowledge.updated_at).getTime())
                          ? formatDistanceToNow(new Date(knowledge.updated_at), { addSuffix: true })
                          : "Just created"}
                      </span>
                      {guestToken && isGuestOwned && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5 text-muted-foreground border-muted-foreground/30">
                          You
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {/* Only show delete button if not guest OR if this guest created the strategy */}
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteKnowledgeId(knowledge.id);
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                      {/* Show Edit for guest-owned or authenticated, View Details for non-guest-owned */}
                      {canEdit ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!useBuilder) { setCompanyEditOpen(true); return; }
                            setBuilderEditId(knowledge.id);
                            setBuilderOpen(true);
                          }}
                        >
                          Edit <ChevronRight className="h-3 w-3 ml-1" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewDetailsKnowledgeId(knowledge.id);
                          }}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

        </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Button
          onClick={onNext}
          disabled={!selectedCampaignKnowledgeId}
          className="gap-2"
        >
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>


      {companyEditOpen && companyProfileId && (
        <CompanyProfileBuilderDialog
          profileId={companyProfileId}
          workspaceId={workspaceId}
          guestToken={guestToken}
          open={companyEditOpen}
          onOpenChange={setCompanyEditOpen}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ["company-profile-facts-status", companyProfileId] })}
        />
      )}

      {builderOpen && (
        <CampaignProfileBuilderDialog
          companyProfileId={companyProfileId}
          workspaceId={workspaceId}
          guestToken={guestToken}
          open={builderOpen}
          editKnowledgeId={builderEditId}
          onOpenChange={(o) => { setBuilderOpen(o); if (!o) setBuilderEditId(null); }}
          onCreated={(id) => {
            queryClient.invalidateQueries({ queryKey: ["campaign-knowledge-by-company", companyProfileId, guestToken] });
            onSelect(id);
          }}
        />
      )}

      {/* View Details Dialog (read-only) */}
      {viewDetailsKnowledgeId && (
        <CampaignKnowledgeDetailsDialog
          knowledgeId={viewDetailsKnowledgeId}
          open={!!viewDetailsKnowledgeId}
          onOpenChange={(open) => !open && setViewDetailsKnowledgeId(null)}
          guestToken={guestToken}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteKnowledgeId} onOpenChange={(open) => !open && setDeleteKnowledgeId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Campaign Strategy</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this campaign strategy? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteKnowledgeId && deleteMutation.mutate(deleteKnowledgeId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
